import { useCurrentPlatform } from "@/contexts";
import { hasCapability } from "@/utils/capability";

import { useActiveScopeCapabilities } from "../capability/useActiveScopeCapabilities";

/**
 * League-surface permissions.
 *
 * - `canSeeLeagues` — the leagues list is a read-open surface: visibility is
 *   decided by the platform-level Tours & Leagues module flag, not a capability
 *   key. This is THE single predicate for the surface — the sidebar entry
 *   (`useNavLinkItemFilter`) and the list page's ForbiddenPage gate both consume
 *   it, so they can never drift apart.
 * - `canCreateLeague` — `LEAGUE:CREATE` on the active scope's capability set.
 * - `canReorderLeagues` — `LEAGUE:WRITE` on the active scope's capability set.
 *   Reorder rewrites the whole scope's manual order (org, or platform in
 *   platform-only mode), so it is gated on the scope-level set, not per-row caps.
 *
 * Strict RBAC — the backend resolver is the single source of truth and the
 * S0 grants give every legacy member the matching key. See docs/capabilities.md.
 */
export const useLeaguePermissions = () => {
  const currentPlatform = useCurrentPlatform();
  const { capabilities } = useActiveScopeCapabilities();

  const canSeeLeagues = currentPlatform.enableToursLeagues;

  const canCreateLeague = hasCapability(capabilities, "LEAGUE:CREATE");

  const canReorderLeagues = hasCapability(capabilities, "LEAGUE:WRITE");

  return {
    canSeeLeagues,
    canCreateLeague,
    canReorderLeagues,
  };
};
